import type { ReactNode } from 'react';
import { Button } from '@carbon/react';
import { ArrowLeft } from '@carbon/react/icons';
import styles from './drill-in-header.module.scss';

export interface DrillInHeaderProps {
  /** Optional uppercase eyebrow line above the title (e.g. the case name). */
  eyebrow?: string;
  /** Optional node rendered inline after the eyebrow (e.g. a "Preview" Tag). */
  eyebrowTag?: ReactNode;
  /** The drill-in's title. */
  title: string;
  /** Optional one-line context beneath the title. */
  subtitle?: string;
  /** Return to the previous panel view. */
  onBack: () => void;
}

/**
 * Shared header for the three Eval Settings drill-ins (case picker / scenario
 * picker / uncovered): a ghost back arrow beside an eyebrow row (+ optional
 * inline tag), the title, and an optional subtitle (mock §10). The back arrow is
 * the ONLY in-drill-in navigation — deliberately no close button; the panel's own
 * header × (always visible above) closes the whole panel (P4 FR#14).
 *
 * @param {DrillInHeaderProps} props - The eyebrow/title/subtitle content + the back handler.
 * @return {JSX.Element} The drill-in header row.
 */
export function DrillInHeader({
  eyebrow,
  eyebrowTag,
  title,
  subtitle,
  onBack,
}: DrillInHeaderProps) {
  return (
    <div className={styles.header}>
      <Button
        className={styles.backButton}
        kind="ghost"
        size="sm"
        hasIconOnly
        renderIcon={ArrowLeft}
        iconDescription="Back"
        tooltipPosition="bottom"
        onClick={onBack}
      />
      <div className={styles.titleBlock}>
        {(eyebrow !== undefined || eyebrowTag !== undefined) && (
          <div className={styles.eyebrowRow}>
            {eyebrow !== undefined && (
              <span className={styles.eyebrow}>{eyebrow}</span>
            )}
            {eyebrowTag}
          </div>
        )}
        <h3 className={styles.title}>{title}</h3>
        {subtitle !== undefined && (
          <span className={styles.subtitle}>{subtitle}</span>
        )}
      </div>
    </div>
  );
}
